import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '../../../src/hooks/useI18nNavigate';
import toast from 'react-hot-toast';
import {
  roadmap_title_required,
  roadmap_courses_required,
  roadmap_updated_success,
  roadmap_created_success
} from '@paraglide/messages';
import {
  useGetRoadmapDetail,
  useCreateRoadmap,
  useUpdateRoadmap,
  type RoadmapDetailCoursesItem,
} from '@edusense/roadmap-client';
import {
  getApiCourses,
  GetApiCoursesType,
  type GetApiCoursesParams,
  type CourseSummary,
  type CourseResponse,
} from '@edusense/course-client';
import type { Course } from '@edusense/course-types';

// UI 표시에 사용될 강의 정보 인터페이스

type RoadmapSelectableCourse =
  | CourseSummary
  | RoadmapDetailCoursesItem
  | CourseResponse
  | (CourseResponse & Partial<CourseSummary>);

export interface DisplayedCourse {
  id: string;
  title?: string;
  description?: string | null;
  instructorDisplay?: string;
  levelDisplay?: '초급' | '중급' | '고급' | '모든 레벨' | '정보 없음';
  thumbnailUrlDisplay?: string;
  originalSummary: RoadmapSelectableCourse;
}

// 강의 레벨 변환 함수
const mapCourseLevelToDisplay = (level?: string): DisplayedCourse['levelDisplay'] => {
  if (!level) return '정보 없음';
  switch (level.toUpperCase()) {
    case 'BEGINNER': return '초급';
    case 'INTERMEDIATE': return '중급';
    case 'ADVANCED': return '고급';
    case 'ALL_LEVELS': return '모든 레벨';
    default: return '정보 없음';
  }
};

const parseNumericValue = (value: unknown): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

const resolveLevelValue = (level: unknown): string | undefined => {
  if (!level) {
    return undefined;
  }
  if (typeof level === 'string') {
    return level;
  }
  if (typeof level === 'object' && 'value' in (level as Record<string, unknown>)) {
    const candidate = (level as Record<string, unknown>).value;
    if (typeof candidate === 'string') {
      return candidate;
    }
  }
  return String(level);
};

const extractMediaFileUrls = (course: RoadmapSelectableCourse): string[] => {
  const urls: string[] = [];
  const mediaFiles = (course as any).mediaFiles;
  if (Array.isArray(mediaFiles)) {
    for (const file of mediaFiles) {
      if (typeof file === 'string' && file) {
        urls.push(file);
      } else if (file && typeof file === 'object') {
        const candidate = (file as any).fileUrl;
        if (typeof candidate === 'string' && candidate) {
          urls.push(candidate);
        }
      }
    }
  }

  const thumbnailUrl = (course as any).thumbnailUrl;
  if (typeof thumbnailUrl === 'string' && thumbnailUrl) {
    urls.push(thumbnailUrl);
  }

  return Array.from(new Set(urls)).filter(Boolean);
};

const deriveInstructorDisplay = (course: RoadmapSelectableCourse): string => {
  const instructor = (course as any).instructor;
  if (!instructor) {
    return '강사 정보 미확인';
  }

  const basicInfo = instructor.basicInfo ?? instructor;
  return (
    basicInfo?.displayName ||
    instructor?.displayName ||
    basicInfo?.username ||
    instructor?.username ||
    basicInfo?.name ||
    '강사 정보 미확인'
  );
};

const isCourseFree = (price: unknown): boolean => {
  if (price == null) {
    return true;
  }

  const amountRaw = (price as any).amount;
  const amount = parseNumericValue(amountRaw);
  if (amount == null) {
    return false;
  }
  return amount === 0;
};

// 다양한 형태의 강의 응답을 Course 타입으로 변환
export const mapCourseSummaryToCourseType = (course: RoadmapSelectableCourse): Course => {
  const progressRateValue = parseNumericValue((course as any).progressRate);
  const mediaFileUrls = extractMediaFileUrls(course);
  const tags: string[] = Array.isArray((course as any).tags)
    ? (course as any).tags.filter((tag: unknown): tag is string => typeof tag === 'string')
    : [];

  return {
    id: String((course as any).id ?? ''),
    title: (course as any).title || '제목 없음',
    description: (course as any).description ?? undefined,
    instructor: (course as any).instructor,
    level: resolveLevelValue((course as any).level) as Course['level'],
    tags,
    price: (course as any).price,
    isFree: isCourseFree((course as any).price),
    enrollmentCount: (course as any).enrollmentCount,
    averageRating: parseNumericValue((course as any).averageRating) ?? (course as any).averageRating,
    reviewCount: parseNumericValue((course as any).reviewCount) ?? (course as any).reviewCount,
    mediaFiles: mediaFileUrls,
    owned: (course as any).owned ?? false,
    progressRate: progressRateValue,
    isCompleted: progressRateValue != null && progressRateValue >= 1,
    createdAt: (course as any).createdAt ? String((course as any).createdAt) : undefined,
  } as Course;
};

// API 응답을 UI에서 사용할 수 있는 형태로 변환
const mapCourseSummaryToDisplayed = (apiCourse: RoadmapSelectableCourse): DisplayedCourse => {
  const levelValue = resolveLevelValue((apiCourse as any).level);
  const mediaUrls = extractMediaFileUrls(apiCourse);

  return {
    id: String((apiCourse as any).id ?? ''),
    title: (apiCourse as any).title ?? undefined,
    description: (apiCourse as any).description ?? undefined,
    instructorDisplay: deriveInstructorDisplay(apiCourse),
    levelDisplay: mapCourseLevelToDisplay(levelValue),
    thumbnailUrlDisplay: mediaUrls[0],
    originalSummary: apiCourse,
  };
};

/**
 * 로드맵 생성/수정을 위한 React Query 기반 커스텀 훅
 * @param roadmapId - 수정 모드일 때의 로드맵 ID (선택적)
 * @returns 폼 상태와 핸들러들
 */
export const useRoadmapCreate = (roadmapId?: string) => {
  const navigate = useNavigate();
  const isEditMode = !!roadmapId;
  const hasValidRoadmapId = isEditMode && !!roadmapId;

  // 폼 상태
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCourses, setSelectedCourses] = useState<DisplayedCourse[]>([]);

  // 검색 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // React Query 훅들
  const { data: roadmapData, isLoading: isLoadingData, error: loadError } = useGetRoadmapDetail(
    roadmapId || '',
    {
      query: {
        enabled: hasValidRoadmapId,
      },
    }
  );

  const isCourseSearchEnabled = debouncedSearchTerm.trim() !== '';

  const {
    data: coursesApiResponse,
    isPending: isLoadingCourses,
    isError: isCoursesError,
    error: coursesError,
  } = useQuery({
    queryKey: ['roadmap-course-search', debouncedSearchTerm.trim()],
    enabled: isCourseSearchEnabled,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const params: GetApiCoursesParams & Record<string, unknown> = {
        type: GetApiCoursesType.FORYOU,
        page: 0,
        size: 20,
      };

      if (isCourseSearchEnabled) {
        params.searchTerm = debouncedSearchTerm.trim();
      }

      return getApiCourses(params as GetApiCoursesParams);
    },
  });

  const courseSearchErrorMessage = coursesError
    ? (coursesError as any)?.response?.data?.message ||
      (coursesError as any)?.message ||
      '강의 목록을 불러오는 중 오류가 발생했습니다.'
    : null;

  const createRoadmapMutation = useCreateRoadmap();
  const updateRoadmapMutation = useUpdateRoadmap();

  // 검색어 디바운싱
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // 수정 모드일 때 기존 데이터 로드
  useEffect(() => {
    if (roadmapData && hasValidRoadmapId) {
      const { summary, courses } = roadmapData;
      setTitle(summary?.title || '');
      setDescription(summary?.description || '');

      if (Array.isArray(courses)) {
        const loadedCourses = courses.map(course => mapCourseSummaryToDisplayed(course));
        setSelectedCourses(loadedCourses);
      }
    }
  }, [roadmapData, hasValidRoadmapId]);

  // 검색 가능한 강의 목록 (이미 선택된 강의 제외)
  const availableCourses = useMemo(() => {
    if (!isCourseSearchEnabled || !coursesApiResponse?.content) {
      return [];
    }

    return coursesApiResponse.content
      .map(mapCourseSummaryToDisplayed)
      .filter(course => !selectedCourses.some(selected => selected.id === course.id));
  }, [coursesApiResponse, selectedCourses, isCourseSearchEnabled]);

  // 에러 메시지 처리
  const error = loadError 
    ? (loadError as any)?.response?.data?.message || 
      (loadError as any)?.message || 
      '로드맵 정보를 불러오는 중 오류가 발생했습니다.'
    : null;

  // 강의 추가 핸들러
  const handleAddCourse = useCallback((course: DisplayedCourse) => {
    if (!selectedCourses.find(sc => sc.id === course.id)) {
      setSelectedCourses(prev => [...prev, course]);
    }
  }, [selectedCourses]);

  // 강의 제거 핸들러
  const handleRemoveCourse = useCallback((courseId: string) => {
    setSelectedCourses(prev => prev.filter(course => course.id !== courseId));
  }, []);

  // 강의 순서 변경 핸들러 (DnD)
  const moveCourse = useCallback((dragIndex: number, hoverIndex: number) => {
    setSelectedCourses(prevCourses => {
      const newCourses = [...prevCourses];
      const [draggedItem] = newCourses.splice(dragIndex, 1);
      newCourses.splice(hoverIndex, 0, draggedItem);
      return newCourses;
    });
  }, []);

  // 뒤로가기 핸들러
  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // 폼 제출 핸들러
  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!title.trim()) {
      toast.error(roadmap_title_required());
      return;
    }
    
    if (selectedCourses.length < 2) {
      toast.error(roadmap_courses_required());
      return;
    }

    const courseIds = selectedCourses.map(course => course.id);

    try {
      if (isEditMode && hasValidRoadmapId) {
        // 수정 모드
        await updateRoadmapMutation.mutateAsync({
          roadmapId: roadmapId!,
          data: {
            title,
            description,
            courseIds,
          },
        });
        toast.success(roadmap_updated_success({title}));
        navigate(`/learn/roadmap/${roadmapId}`);
      } else {
        // 생성 모드
        const response = await createRoadmapMutation.mutateAsync({
          data: {
            title,
            description,
            courseIds,
          },
        });

        // Axios response 구조: { data, status, headers }
        // customInstance<string>이 반환하는 값은 axios response의 data 부분
        let createdRoadmapId: string | null = null;

        if (typeof response === 'string') {
          // 직접 string이 반환된 경우
          createdRoadmapId = response;
        } else if (response && typeof response === 'object') {
          // Axios response object인 경우
          const data = (response as any).data;
          createdRoadmapId = typeof data === 'string' ? data : null;
        }

        console.log('🔍 Create roadmap response:', response);
        console.log('🔍 Extracted roadmapId:', createdRoadmapId);

        toast.success(roadmap_created_success({title}));

        if (createdRoadmapId && createdRoadmapId !== 'new') {
          navigate(`/learn/roadmap/${createdRoadmapId}`);
        } else {
          console.warn('⚠️ No valid roadmap ID received, navigating to list');
          navigate('/learn/roadmaps');
        }
      }
    } catch (err: any) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} roadmap:`, err);
      const errorMsg = err?.response?.data?.message || `로드맵 ${isEditMode ? '수정' : '생성'} 중 오류가 발생했습니다.`;
      toast.error(errorMsg);
    }
  }, [
    title,
    description,
    selectedCourses,
    isEditMode,
    roadmapId,
    hasValidRoadmapId,
    updateRoadmapMutation,
    createRoadmapMutation,
    navigate
  ]);

  return {
    // 상태
    title,
    description,
    searchTerm,
    selectedCourses,
    availableCourses,
    isEditMode,
    
    // 로딩 상태
    isLoadingData: isLoadingData && hasValidRoadmapId,
    isLoadingCourses,
    isSubmitting: createRoadmapMutation.isPending || updateRoadmapMutation.isPending,
    
    // 에러
    error,
    courseSearchErrorMessage,
    hasCourseSearchError: isCoursesError,
    
    // 핸들러
    setTitle,
    setDescription,
    setSearchTerm,
    handleAddCourse,
    handleRemoveCourse,
    moveCourse,
    handleGoBack,
    handleSubmit,
  };
};
